import * as React from 'react'
import { CF_ASSET_MIME, URI_LIST_MIME, serializeCfAssetDrag } from './asset'
import type { CfAssetDrag } from './asset'
import { setCurrentAssetDrag } from './current-drag'
import { DRAG_THUMB_SIZE, dragThumbSource, paintDragThumb } from './drag-thumb'

// Child images, video, and links start their own drag and would replace this payload.
export const ASSET_DRAG_SOURCE_CLASS =
  '[&_a]:[-webkit-user-drag:none] [&_img]:[-webkit-user-drag:none] [&_video]:[-webkit-user-drag:none]'

export function useAssetDrag(asset: CfAssetDrag | null) {
  const assetRef = React.useRef(asset)
  assetRef.current = asset
  const thumbRef = React.useRef<HTMLCanvasElement | null>(null)
  const thumbReadyRef = React.useRef(false)
  const source = asset ? dragThumbSource(asset) : null
  const videoThumb = asset?.mediaType === 'video'

  React.useEffect(() => {
    thumbReadyRef.current = false
    if (!source || typeof document === 'undefined') {
      return
    }
    const canvas = document.createElement('canvas')
    canvas.width = DRAG_THUMB_SIZE
    canvas.height = DRAG_THUMB_SIZE
    canvas.setAttribute('aria-hidden', 'true')
    // Safari ignores setDragImage unless the element is in the document.
    canvas.style.position = 'fixed'
    canvas.style.left = '-1000px'
    canvas.style.top = '0'
    canvas.style.pointerEvents = 'none'
    document.body.append(canvas)
    thumbRef.current = canvas
    let cancelled = false
    const image = new Image()
    image.onload = () => {
      if (cancelled) {
        return
      }
      thumbReadyRef.current = paintDragThumb(
        canvas,
        image,
        videoThumb ? { video: true } : undefined,
      )
    }
    image.src = source
    return () => {
      cancelled = true
      thumbReadyRef.current = false
      canvas.remove()
      if (thumbRef.current === canvas) {
        thumbRef.current = null
      }
    }
  }, [source, videoThumb])

  function onDragStart(event: React.DragEvent) {
    const current = assetRef.current
    if (!current) {
      return
    }
    event.dataTransfer.setData(CF_ASSET_MIME, serializeCfAssetDrag(current))
    event.dataTransfer.setData(URI_LIST_MIME, current.url)
    event.dataTransfer.effectAllowed = 'copy'
    setCurrentAssetDrag(current)
    const canvas = thumbRef.current
    if (thumbReadyRef.current && canvas) {
      const hotspot = DRAG_THUMB_SIZE / 2
      event.dataTransfer.setDragImage(canvas, hotspot, hotspot)
    }
  }

  function onDragEnd() {
    setCurrentAssetDrag(null)
  }

  return {
    draggable: asset !== null,
    onDragStart,
    onDragEnd,
  }
}
